<script>
// Article autocomplete of Saisie rapide: same suggestions, selection and look for the entry line and the "Lignes erronées" rows.
// Attributes and listeners not declared here (dropdown-should-open, @search, @open, @input...) go straight to v-select, so v-model works as on v-select.
export default {
  name: "QuickOrderItemSelect",
  inheritAttrs: false,
  props: {
    value: {
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    getOptionLabel: {
      type: Function,
      default: option => (option && option.title) || ""
    },
    // colours the selected article like its row (panache lines)
    highlighted: {
      type: Boolean,
      default: false
    },
    // shown instead of "Aucun article dans la liste" while the caller is still searching (unset: unchanged)
    searchingText: {
      type: String,
      default: ""
    },
  },

  computed: {
    isAvnClient() {
      return process.env.VUE_APP_CLIENT_NAME === "avn";
    },
  },

  methods: {
    // open or close the suggestions from the parent, as $refs.select.open on a bare v-select
    setOpen(value) {
      if (this.$refs.select) {
        this.$refs.select.open = value
      }
    },

    remainingControlledQuantity(maxPerDay, alreadyOrderedPerDay) {
      const remainingQuantity = maxPerDay - alreadyOrderedPerDay
      return remainingQuantity < 0 ? 0 : remainingQuantity
    },

    selectedTitle(item) {
      return [item.barcode || item.ref, item.title].filter(Boolean).join(" - ")
    },

    // the menu is appended to <body>: never narrower than the suggestion layout needs, never past the window
    positionMenu(menu, component, { width, top, left }) {
      const room = document.documentElement.clientWidth - (parseFloat(left) || 0) - 16
      menu.style.top = top
      menu.style.left = left
      menu.style.width = Math.max(parseFloat(width) || 0, Math.min(640, room)) + "px"
    },
  },
}
</script>

<template>
  <v-select
    ref="select"
    v-bind="$attrs"
    :value="value"
    :options="options"
    :filterable="false"
    :get-option-label="getOptionLabel"
    :append-to-body="true"
    :calculate-position="positionMenu"
    class="v-select-auto-complete qo-item-select"
    v-on="$listeners"
  >
    <!-- a caller may replace the suggestion layout (demande d'avoir: no price or stock) -->
    <template v-if="$scopedSlots.option" #option="item">
      <slot name="option" v-bind="item" />
    </template>
    <template v-else #option="item">
      <div class="grid-option row pt-1 pb-2 qo-option" :class="{ 'qo-option--avn': isAvnClient }">
        <div class="col-2">
          <div class="font-size-12 font-weight-bold text-dark qo-option__ref">{{ item.ref }}</div>
          <div v-if="item.barcode" class="font-size-10 text-dark qo-option__barcode">{{ item.barcode }}</div>
        </div>

        <div class="col-6">
          <span class="font-size-12 font-weight-bold qo-option__title" :class="item.gratuities && item.gratuities.length ? 'text-primary' : 'text-dark'" style="cursor: pointer;">
            {{ item.title }}
            <sup>
              <i v-if="item.isFrigo" class="fas fa-snowflake ml-2 font-size-14 text-primary font-weight-bold" title="Produit Réfrigérateur" style="border-radius:5px;"></i>
            </sup>
          </span>
          <div v-if="item.pharmaDci" class="font-size-12 font-weight-normal qo-option__dci" :class="item.gratuities && item.gratuities.length ? 'text-primary' : 'text-dark'">
            {{ item.pharmaDci }}
          </div>
          <div class="font-size-10 text-dark qo-option__meta">
            {{ item.packageCount ? item.packageCount + ' / carton' : '-' }}
          </div>

          <div>
            <span v-if="item.expirationDate" class="text-dark qo-option__meta" style="font-size:12px;" :title="'Date de péremption : ' + item.expirationDate">DP {{ item.expirationDate }}</span>
          </div>

          <span v-for="(itemGrat, index) in item.gratuities" :key="index" class="mr-1 badge badge-primary text-white font-weight-bold">
            <i class="fas fa-star mr-1"/>{{ itemGrat.gratuity }} +{{ itemGrat.gratuityGranted }}
          </span>
        </div>

        <div class="col-1 my-auto text-center qo-option__stock">
          <i v-if="item.stockStatus==2" :title="$t('catalog.stock.notAvailable')" class="fas fa-times-circle text-danger font-size-18"> </i>
          <div v-else-if="item.stockStatus==3" class="font-size-18"> _</div>
          <i v-else-if="item.stockStatus==0 || item.stockStatus==1" :title="$t('catalog.stock.available')" class="fas fa-check-circle text-success font-size-18"> </i>
          <div v-else-if="item.stockStatus==4" title="Vente contrôlée" class="text-info font-weight-bold font-size-14">
            <span :class="item.alreadyOrderedPerDay ? 'strikethrough text-dark mr-1' : ''">{{ item.maxPerDay }}</span>
            <span v-if="item.alreadyOrderedPerDay">{{ remainingControlledQuantity(item.maxPerDay, item.alreadyOrderedPerDay) }}</span>
            {{ '/' + ((item.salesControlDayCount && item.salesControlDayCount > 1) ? item.salesControlDayCount : '') + 'J' }}
          </div>
          <div v-else-if="item.stockStatus == 5" class="d-flex align-items-center justify-content-center">
            <span style="border-radius: 5px;" title="Article en répartition"
            class="text-primary p-1 font-weight-bold font-size-16 d-flex align-items-center justify-content-center text-center"
            >{{ item.repartitionQuantity }}</span>
          </div>
        </div>

        <div class="col-2 my-auto text-right qo-option__prices">
          <div v-if="item.priceTaxIncl" :style="item.vat || item.pricePublic ? 'margin:2px' : ''" class="text-dark font-weight-bold qo-option__price"><span class="qo-option__price-label">Prix TTC</span>{{ item.priceTaxIncl }}</div>
          <div v-if="item.pricePublic" class="text-dark font-weight-normal qo-option__small" style="font-size:10px;"><span class="qo-option__price-label">Prix public</span>{{ item.pricePublic }}</div>
          <div v-if="item.vat" class="text-dark font-weight-normal qo-option__small" style="font-size:10px;">{{ item.vat ? 'TVA: ' + item.vat + '%' : '' }}</div>
        </div>
      </div>
    </template>

    <template #selected-option="item">
      <span class="qo-item-select__selected" :class="{ 'text-primary': highlighted }" :title="selectedTitle(item)">{{ item.title }}</span>
    </template>

    <!-- a caller may replace the search input (the error rows show the code being fixed) -->
    <template v-if="$scopedSlots.search" #search="search">
      <slot name="search" v-bind="search" />
    </template>

    <template #no-options>
      <span v-if="searchingText" class="qo-option__empty"><b-spinner small class="mr-2" />{{ searchingText }}</span>
      <span v-else class="text-danger qo-option__empty">Aucun article dans la liste</span>
    </template>
  </v-select>
</template>

<style>
/* the field (v-select internals, hence not scoped) */
.qo-item-select { font-size: 1.24em; }
.qo-item-select .vs__actions { display: none; }
.v-select-auto-complete.qo-item-select.vs--searchable .vs__dropdown-toggle { min-height: 42px; border: 1px solid #d0d5dd; border-radius: 8px !important; background: #fff; }
.v-select-auto-complete.qo-item-select.vs--open .vs__dropdown-toggle,
.v-select-auto-complete.qo-item-select .vs__dropdown-toggle:focus-within { border-color: #34469c; box-shadow: 0 0 0 3px rgba(52, 70, 156, .12); }
.qo-item-select .vs__selected { min-width: 0; max-width: 100%; font-weight: 600; color: #101828; }
.qo-item-select__selected { display: block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* the suggestion menu is appended to <body>: style it only when it holds article suggestions */
.vs__dropdown-menu:has(.qo-option) { padding: .25rem; border: 1px solid #e4e7ec; border-radius: 10px; box-shadow: 0 12px 24px -6px rgba(16, 24, 40, .16), 0 4px 8px -2px rgba(16, 24, 40, .08); }
.vs__dropdown-option:has(.qo-option) { padding: 0 .5rem; border-radius: 8px; white-space: normal; }
.vs__dropdown-option--highlight:has(.qo-option) { background: #eef2fb; color: inherit; }
.vs__dropdown-option--highlight:has(.qo-option--avn) { background: #1b6fa8; color: #fff; }
/* avn: strong highlight, so every text and icon of the highlighted line goes white, except the stock column which keeps its colours (the greys are !important and scoped, hence the long selector) */
.vs__dropdown-menu .vs__dropdown-option.vs__dropdown-option--highlight:has(.qo-option--avn) .qo-option--avn > :not(.qo-option__stock),
.vs__dropdown-menu .vs__dropdown-option.vs__dropdown-option--highlight:has(.qo-option--avn) .qo-option--avn > :not(.qo-option__stock) * { color: #fff !important; }
.vs__dropdown-menu .vs__dropdown-option.vs__dropdown-option--highlight:has(.qo-option--avn) .qo-option--avn .badge { background: #fff !important; color: #1b6fa8 !important; }
.vs__dropdown-menu .vs__dropdown-option.vs__dropdown-option--highlight:has(.qo-option--avn) .qo-option--avn .badge * { color: #1b6fa8 !important; }
.vs__dropdown-option:has(.qo-option):last-child .qo-option { border-bottom: 0; }
</style>

<style scoped>
/* the suggestions */
.qo-option { margin: 0; padding: .6rem .35rem; border-bottom: 1px solid #f2f4f7; align-items: center; }
.qo-option__ref { color: #344054 !important; font-size: 12px !important; letter-spacing: .02em; }
.qo-option__barcode { margin-top: .1rem; color: #98a2b3 !important; font-size: 11px !important; font-variant-numeric: tabular-nums; }
.qo-option__title { font-size: 14px !important; font-weight: 600 !important; line-height: 1.35; }
.qo-option__title.text-dark { color: #101828 !important; }
.qo-option__dci { margin-top: .1rem; font-size: 12.5px !important; }
.qo-option__dci.text-dark { color: #475467 !important; }
.qo-option__meta { color: #667085 !important; font-size: 12px !important; }
.qo-option__prices { font-variant-numeric: tabular-nums; }
.qo-option__price { color: #101828 !important; font-size: 14px; }
.qo-option__small { color: #667085 !important; font-size: 11px !important; }
/* price captions in the suggestions: "Prix TTC" / "Prix public" in front of each amount */
.qo-option__price-label { margin-right: .35rem; color: #667085; font-size: 10.5px; font-weight: 600; letter-spacing: .01em; }
.qo-option__empty { display: block; padding: .6rem .5rem; color: #667085 !important; font-size: 13px; }
</style>
